"use client";
import Fuse from "fuse.js";
import React, { useEffect, useMemo, useState } from "react";
import { HiMagnifyingGlass } from "react-icons/hi2";
import { Badge } from "~/components/shadcn/Badge";
import { Button } from "~/components/v3/themed/Button";
import { GooeySearchInput } from "~/components/v3/themed/SearchInput";
import { DiscoveryCommunity, WaitlistCommunity } from "../actions";
import CommunityDiscoveryCards from "./CommunityDiscoveryCard";
import CommunityWaitlistCards from "./CommunityWaitlistCard";
import { SelectCategoryResponsive } from "./SelectCategoryResponsive";

interface CommunityDiscoveryClientProps {
    title: string;
    communities: DiscoveryCommunity[] | WaitlistCommunity[];
    waitlistedCommunities?: WaitlistCommunity[];
    renderCommunities: (filteredCommunities: any) => React.ReactNode;
}

export function CommunityDiscoveryClient({
    title,
    communities,
    renderCommunities,
    waitlistedCommunities,
}: CommunityDiscoveryClientProps) {
    // Search and filter state
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
    const [isSearching, setIsSearching] = useState(false);

    // Extract all unique categories
    const allCategories = useMemo(() => {
        const categorySet = new Set<string>();
        communities.forEach((community) => {
            community.categories?.forEach((category) => {
                if (category) categorySet.add(category);
            });
        });
        return Array.from(categorySet).sort();
    }, [communities]);

    // Setup fuse for fuzzy search
    const fuse = useMemo(() => {
        let results: (DiscoveryCommunity | WaitlistCommunity)[] = communities;
        if (searchQuery.trim() !== "" && waitlistedCommunities) {
            results = [...results, ...waitlistedCommunities];
        }
        // Create a type-safe version for Fuse that only includes the fields we search on
        const searchableItems = results.map((community) => ({
            name: community.name,
            slug: community.slug,
            description: community.description,
            // Include a reference to the original item
            original: community,
        }));

        return new Fuse(searchableItems, {
            keys: ["name", "slug", "description"],
            threshold: 0.4,
            includeScore: true,
        });
    }, [communities, searchQuery, waitlistedCommunities]);

    // Filter communities based on search query and selected categories
    const filteredCommunities = useMemo(() => {
        setIsSearching(true);
        try {
            let results: (DiscoveryCommunity | WaitlistCommunity)[] =
                communities;
            if (searchQuery.trim() !== "" && waitlistedCommunities) {
                results = [...results, ...waitlistedCommunities];
            }

            // Apply search filter if query exists
            if (searchQuery.trim()) {
                const searchResults = fuse.search(searchQuery);
                results = searchResults.map(
                    (result) => result.item.original,
                ) as any;
            }
            // Apply category filter
            if (selectedCategories.length > 0) {
                results = results.filter((community) =>
                    community.categories?.some((category) =>
                        selectedCategories.includes(category),
                    ),
                ) as any;
            }
            return results as any;
        } finally {
            // Small timeout to prevent UI jank
            setTimeout(() => setIsSearching(false), 100);
        }
    }, [
        communities,
        fuse,
        searchQuery,
        selectedCategories,
        waitlistedCommunities,
    ]);

    // Handle search input change
    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setIsSearching(true);
        setSearchQuery(e.target.value);
    };

    // Toggle category selection
    const toggleCategory = (category: string) => {
        setSelectedCategories((prev) =>
            prev.includes(category)
                ? prev.filter((c) => c !== category)
                : [...prev, category],
        );
    };

    // if category is selected in the url, add it to the selected categories
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const category = urlParams.get("category");
        if (category) {
            setSelectedCategories((prev) => [...prev, category]);
        }
    }, []);

    // Remove a single category from selection
    const removeCategory = (category: string) => {
        setSelectedCategories((prev) => prev.filter((c) => c !== category));
    };

    // Clear all selected categories
    const clearCategories = () => {
        setSelectedCategories([]);
    };

    return (
        <div className="mb-4 flex w-full flex-col items-start">
            <div className="mb-3 mt-5 flex w-full flex-col items-start justify-between gap-3 md:flex-row md:items-center">
                <h2 className="text-xl font-semibold text-white">{title}</h2>
                <div className="hidden w-full flex-row gap-2 md:flex md:w-auto">
                    <SelectCategoryResponsive
                        categories={allCategories}
                        selectedCategories={selectedCategories}
                        onToggleCategory={toggleCategory}
                    />
                </div>
            </div>
            <div className="mb-5 flex w-full min-w-[284px] items-center justify-between gap-2 md:w-auto md:flex-row">
                {/* Search input */}
                <div className="w-full">
                    <GooeySearchInput
                        placeholder="Search communities"
                        value={searchQuery}
                        onChange={handleSearch}
                        iconComponent={
                            <HiMagnifyingGlass className="mr-2 h-4 w-4" />
                        }
                        isLoading={isSearching}
                        autoFocus
                        className="w-full max-w-[384px] rounded-lg"
                    />
                </div>
                <div className="flex w-max md:hidden">
                    <SelectCategoryResponsive
                        categories={allCategories}
                        selectedCategories={selectedCategories}
                        onToggleCategory={toggleCategory}
                    />
                </div>
            </div>
            {/* Selected category badges */}
            {selectedCategories.length > 0 && (
                <div className="mb-4 mt-4 flex flex-wrap items-center gap-2">
                    {selectedCategories.map((category, i) => (
                        <Badge
                            key={category + i}
                            variant="secondary"
                            className="flex items-center gap-1"
                        >
                            {category}
                            <button
                                className="ml-1 rounded-full hover:bg-muted"
                                onClick={() => removeCategory(category)}
                                aria-label={`Remove ${category} filter`}
                            >
                                x
                            </button>
                        </Badge>
                    ))}
                    {selectedCategories.length > 1 && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={clearCategories}
                            className="h-7 px-2 text-xs"
                        >
                            Clear all
                        </Button>
                    )}
                </div>
            )}
            {/* Render the filtered communities */}
            {renderCommunities(filteredCommunities)}
            {/* Empty state */}
            {filteredCommunities.length === 0 && !isSearching && (
                <div className="w-full py-8 text-center">
                    <p className="text-v3-N-300">
                        No communities found matching your criteria.
                    </p>
                    <Button
                        variant="link"
                        onClick={() => {
                            setSearchQuery("");
                            setSelectedCategories([]);
                        }}
                        className="mt-2"
                    >
                        Clear filters
                    </Button>
                </div>
            )}
        </div>
    );
}

export function WaitlistedCommunityDiscoveryClient({
    title,
    communities,
    renderCommunities,
}: CommunityDiscoveryClientProps) {
    return (
        <div className="mb-4 flex w-full flex-col items-start">
            {renderCommunities(communities)}
        </div>
    );
}

interface CommunityDiscoveryClientContainerProps {
    title: string;
    initialCommunities: DiscoveryCommunity[] | WaitlistCommunity[];
    waitlistedCommunities?: WaitlistCommunity[];
}

export function CommunityDiscoveryClientContainer({
    title,
    initialCommunities,
    waitlistedCommunities,
}: CommunityDiscoveryClientContainerProps) {
    return (
        <CommunityDiscoveryClient
            title={title}
            communities={initialCommunities}
            waitlistedCommunities={waitlistedCommunities}
            renderCommunities={(filteredCommunities) => (
                <CommunityDiscoveryCards communities={filteredCommunities} />
            )}
        />
    );
}

export function WaitlistCommunityDiscoveryClient({
    title,
    initialCommunities,
}: CommunityDiscoveryClientContainerProps) {
    return (
        <WaitlistedCommunityDiscoveryClient
            title={title}
            communities={initialCommunities}
            renderCommunities={(filteredCommunities) => (
                <CommunityWaitlistCards communities={filteredCommunities} />
            )}
        />
    );
}
